import React from 'react';
import { Card, Typography, Row, Col, Skeleton, Empty } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useCampaignSubmissions } from '../../../services/TaskService';
import { Submission } from 'services/types/TaskTypes';
import styles from '../Profile.module.css';
import { Enrollment } from 'models/CommonType';

const { Title, Text } = Typography;

// Utility functions for TaskTypes Submission
const SubmissionUtils = {
  hasImages: (submission: Submission): boolean => {
    return !!(submission.images && submission.images.length > 0);
  },

  getFirstImage: (submission: Submission) => {
    if (submission.images && submission.images.length > 0) {
      return submission.images[0];
    }
    return null;
  },

  getImageCount: (submission: Submission): number => {
    return submission.images?.length || 0;
  }
};

interface MemberOfSectionProps {
  enrollments: Enrollment[];
  isLoading: boolean;
  authKey: string;
  userId: string;
}

const MemberOfSection: React.FC<MemberOfSectionProps> = ({
  enrollments,
  isLoading,
  authKey,
  userId
}) => {
  const { t } = useTranslation();

  if (!isLoading && (!enrollments || enrollments.length === 0)) {
    return null;
  }

  return (
    <Card className={styles.sectionCard} bordered={false}>
      <div className={styles.sectionHeader}>
        <Title level={4} className={styles.sectionTitle}>
          {t('#Profile.member_of')}
        </Title>
      </div>

      <div className={styles.memberOfGrid}>
        <Row gutter={[24, 24]}>
          {isLoading
            ? Array.from({ length: 3 }).map((_, index) => (
              <Col xs={24} sm={12} lg={8} key={index}>
                <Card className={styles.campaignCard} loading={true} />
              </Col>
            ))
            : enrollments.map((enrollment) => (
              <Col xs={24} sm={12} lg={8} key={enrollment.id}>
                <CampaignCard
                  enrollment={enrollment}
                  userId={userId}
                  authKey={authKey}
                />
              </Col>
            ))
          }
        </Row>
      </div>

      {!isLoading && enrollments.length === 0 && (
        <div className={styles.emptyState}>
          <Text className={styles.emptyText}>
            {t('#Profile.no_campaigns')}
          </Text>
        </div>
      )}
    </Card>
  );
};

interface CampaignCardProps {
  enrollment: Enrollment;
  userId: string;
  authKey: string;
}

const CampaignCard: React.FC<CampaignCardProps> = ({ enrollment, userId, authKey }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    data: submissions,
    isLoading: isSubmissionsLoading
  } = useCampaignSubmissions(userId, enrollment.campaign.id, authKey);

  const handleCampaignClick = () => {
    navigate(`/campaign?id=${enrollment.campaign.id}`);
  };

  return (
    <Card className={styles.campaignCard} bordered={false}>
      {/* Campaign Header - Clickable */}
      <div
        className={styles.campaignHeader}
        onClick={handleCampaignClick}
        style={{ cursor: 'pointer' }}
      >
        <div className={styles.campaignImageContainer}>
          {enrollment.campaign.image_small_url ? (
            <img
              src={enrollment.campaign.image_small_url}
              alt={enrollment.campaign.name}
              className={styles.campaignImage}
            />
          ) : (
            <div className={styles.campaignImagePlaceholder}>
              <div className={styles.placeholderContent}>
                {enrollment.campaign.name.charAt(0).toUpperCase()}
              </div>
            </div>
          )}
        </div>

        <div className={styles.campaignInfo}>
          <Title level={5} className={styles.campaignTitle}>
            {enrollment.campaign.name}
          </Title>
        </div>
      </div>

      {/* Submissions Section */}
      <div className={styles.submissionsSection}>
        <div className={styles.submissionsSectionHeader}>
          <Text className={styles.submissionsSectionTitle}>
            {t('#Profile.submissions')}
          </Text>
          <Text className={styles.submissionsCount}>
            {submissions?.length || 0}
          </Text>
        </div>

        {isSubmissionsLoading ? (
          <div className={styles.submissionsGrid}>
            {Array.from({ length: 6 }).map((_, index) => (
              <Skeleton.Avatar
                key={index}
                active
                size={80}
                shape="square"
                className={styles.submissionThumbnailSkeleton}
              />
            ))}
          </div>
        ) : submissions && submissions.length > 0 ? (
          <div className={styles.submissionsGrid}>
            {submissions.map((submission: Submission) => (
              <SubmissionThumbnail
                key={submission.id}
                submission={submission}
              />
            ))}
          </div>
        ) : (
          <div className={styles.noSubmissions}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Text className={styles.noSubmissionsText}>
                  {t('#Profile.no_submissions')}
                </Text>
              }
            />
          </div>
        )}
      </div>
    </Card>
  );
};

interface SubmissionThumbnailProps {
  submission: Submission;
}

const SubmissionThumbnail: React.FC<SubmissionThumbnailProps> = ({ submission }) => {
  const hasImages = SubmissionUtils.hasImages(submission);

  if (!hasImages) {
    return (
      <div className={styles.submissionThumbnail}>
        <div className={styles.submissionPlaceholder}>
          <div className={styles.submissionPlaceholderContent}>
            {submission.task?.name?.charAt(0).toUpperCase() || 'S'}
          </div>
        </div>
      </div>
    );
  }

  const firstImage = SubmissionUtils.getFirstImage(submission);
  const imageCount = SubmissionUtils.getImageCount(submission);

  return (
    <div className={styles.submissionThumbnail}>
      <img
        src={firstImage?.image_small_url}
        alt={submission.task?.name || 'Submission'}
        className={styles.submissionImage}
      />

      {/* Multiple images indicator */}
      {imageCount > 1 && (
        <div className={styles.multiImageIndicator}>
          <span className={styles.imageCountText}>
            +{imageCount - 1}
          </span>
        </div>
      )}
    </div>
  );
};

export default MemberOfSection;